// THE RUN ITSELF — one row per failure kind in this family.
// See `../failure-kind.ts` for how the four families become `TaskFailureKind`.

import { TaskHoldReason } from '../hold';
import {
  BabysitRoundCharge,
  charged,
  type FailureKindRow,
  platform,
  SCREEN_UNAVAILABLE_RECHECK_MS,
  TASK_MAX_ATTEMPTS,
  TaskExhaustionAction,
  terminal,
  uncharged,
  unchargedOutage,
  wire,
} from './row';

/**
 * THE RUN ITSELF — the residual, the platform's own span throws, and the bounds that stop a run that is alive but useless.
 *
 * Each row is the WHOLE decision for its kind — who writes it, what it costs, whether it
 * parks, whose fault it is and what a reader is told. The doc on each key is the kind's
 * own docblock followed by the reasoning that used to sit beside its entry in each of the
 * per-concern tables (`TASK_FAILURE_PRODUCER`, `TASK_FAILURE_BUDGET`, the box-local set,
 * the re-pend hold, `BABYSIT_ROUND_CHARGE`), moved verbatim.
 */
export const RUN_FAILURE_KINDS = {
  /**
   * The box streamed a terminal `error` run event whose payload parses the wire
   * `ErrorPayloadSchema`. Transient BY POLICY: the runner emits this for clone
   * failures, adapter timeouts, OOM kills and provider 5xx alike (today's wire
   * carries a free-text `message` and no machine `code`), and that population is
   * dominated by faults a fresh box clears — bounded by {@link TASK_MAX_ATTEMPTS}
   * so a deterministic failure still terminates after three runs.
   *
   * AFTER THE 2026-08-29 RE-PARTITION IT IS THE RESIDUAL, and that is what makes
   * it readable: a row lands here because the box threw and NO named seam
   * claimed it.
   *
   * THE DEFERRED CONTEXT-OVERFLOW LINE WAS TAKEN ON 2026-09-01. This paragraph
   * used to end by leaving one signature here on purpose — `planner error:
   * Invalid request: Your request exceeded model token limit: 1048576`, ONE
   * occurrence in three days — on the ground that a single occurrence is not a
   * population, and to revisit it when it had a trend. It has one:
   * {@link ContextWindowExceeded} carries the evidence. The deferral is recorded
   * as TAKEN rather than deleted, because the next reader otherwise re-derives
   * the same argument and re-defers the same fault.
   *
   * THE RESIDUAL IS STILL THE LARGEST NAMED KIND, AND IT IS NOT A BACKLOG. This
   * paragraph argued the opposite for two weeks and the argument has expired, so it
   * is corrected rather than extended (#4181). It read: 1,478 of 2,234 all-time
   * `failed` rows, a daily series of 177 → 21 → 7 → 2 over 2026-08-29 → 09-01 as the
   * slice-12 producers came live, therefore "a BACKLOG of rows stamped before their
   * kinds had producers, not a live population". Prod on 2026-09-16 refutes each
   * half: **1,774 `failed` rows carry this kind, 1,756 of them inside the last 30
   * days** (of 2,888 `failed` rows in that window), and the series did not stay
   * collapsed — over the last twelve days it runs 2-42 a day, ~12/day on average.
   * The all-time count is not a frozen tail, it is this month's traffic. Warning the
   * next reader about a missing time bound while asserting a shape the bounded query
   * no longer supports is the same defect the paragraph was written to prevent.
   *
   * WHAT IS LEFT IN THE BUCKET, measured the same day over uncoded `error` events on
   * tasks graded here (30 days, so the next slice starts from a list and not a total).
   * The event volume is heavily FRONT-LOADED — 300-570/day through 2026-08-29, then
   * 2-138/day as the re-partition, #2570/#2572, #3050, #4021 and slice-12 producers
   * reached the fleet — so a 30-day total measures the rollouts, not today. The live
   * remainder, by cause:
   *   - **no kind names it**: the pre-task hygiene disk floor (134 events / 52 tasks,
   *     `loop.ts` fails the assignment with a free-text reason and no code, #4094); a
   *     provider 400 refusing our own `max_tokens` range (54 / 23); a turn the
   *     provider truncated at its own output cap (61 / 39); a conflict-resolution
   *     lane's refusal (15 / 9). Each needs a NEW member, i.e. a budget decision.
   *   - **the kind exists and its LADDER is wrong for this producer**: the babysit
   *     follow-up's `remote_moved` refusal (47 / 48) is {@link TreeDiverged} by cause
   *     but 16 of those 48 tasks went on to `completed`, and that kind is budgeted to
   *     ONE attempt; the `gh: Bad credentials` population (13 / 40, 7 recovered) is
   *     {@link GitAuth} by cause under the same one-attempt ladder. Naming either
   *     would end a retry-recovering population on its first fault — #3164's lesson,
   *     which is that two producers with opposite retry semantics need two kinds.
   *   - **already closed, still in the 30-day window**: the withdrawn-model farewell,
   *     the pool-cooling prose, the team lane's `could not read Username`, the
   *     transport timeout and the unreadable 2xx. Each stops dead at the date its
   *     producer reached the fleet, which is why a 30-day decomposition reads like a
   *     list of open defects and is mostly a list of shipped ones.
   *
   * ONE ENTRY ON THAT LAST LIST WAS WRONG, and the correction is the lesson rather
   * than the erratum. "The pool-cooling prose" was filed as CLOSED because its
   * producer had landed — `SpentPoolKeyError` stamps `err.code` at the throw, which
   * is the whole #2570 convention. But a STAMP is not a GRADE: the code was not a
   * {@link TaskFailureKind} member, so {@link classifyTaskFailure}'s enumeration
   * lookup missed it and every row came straight back here, with the full charged
   * ladder. Two more had accumulated the same way —
   * `survey_empty` and `max_output_unknown` — and all three are named as
   * {@link PoolKeySpent}, {@link SurveyEmpty} and {@link MaxOutputUnknown}. So: a
   * producer that stamps a code nobody budgeted is indistinguishable, from this
   * bucket, from a producer that stamps nothing at all. `apps/runner/src/
   * failure-wire-code.test.ts` is where that is now checked per throw class.
   *
   * Producer:
   * The residual, and after this partition that is all it is: the box threw and
   * no named seam claimed it. `classifyTaskFailure` writes it for a wire-valid
   * payload carrying no code, or a code this platform has not budgeted.
   *
   * Babysit round:
   * The residual — box faults and work faults alike; cannot tell, so it charges. The
   * provider's `max_tokens`-range refusal still lands here untyped (see the
   * `runner_error` docblock); give it a kind before excusing it, never match its prose.
   *
   * Box-at-fault:
   * The box threw and NO named seam claimed it. This is the residual, and it
   * is the original meaning of this gate.
   */
  RunnerError: {
    value: 'runner_error',
    ordinal: 0,
    producer: platform('packages/domain/src/entities/task.ts#classifyTaskFailure'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The worker box threw while running the task and no named cause claimed it — the run timeline carries the box's own error.",
      unclaimed:
        'No box ever claimed this task — a worker box threw on the only kind of work it was offered, so the platform turned the row away before dispatch.',
      ran: 'A worker box threw while running the task and no named cause claimed it — the run timeline carries the box’s own error.',
    },
  },
  /**
   * The `error` event's payload failed the wire schema — we cannot say what
   * happened, so we do not spend another paid run on it. Fail fast: an
   * unclassifiable failure is TERMINAL, never defaulted to retryable.
   *
   * Producer:
   * Written by the classifier itself when the wire payload fails its schema.
   *
   * Budget:
   * ONE attempt, i.e. terminal on the first occurrence — the same verdict
   * `TASK_FAILURE_RETRYABLE` gave it, now stated as the budget it always was.
   * Fail fast: an unclassifiable failure must not spend another paid run.
   *
   * Babysit round:
   * The error payload failed the wire schema — unknown cause charges.
   *
   * Box-at-fault:
   * The box's own runner binary emitted an `error` payload that fails OUR
   * OWN wire schema. Nothing off-box can make our agent do that: it is a box
   * running a broken or stale build — the false-green class this page exists
   * for. It is also terminal on the first occurrence, so it never floods.
   */
  ErrorUnclassified: {
    value: 'error_unclassified',
    ordinal: 3,
    producer: platform('packages/domain/src/entities/task.ts#classifyTaskFailure'),
    budget: charged(1, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: true,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The box's failure report did not parse, so we cannot say what went wrong and will not spend another paid run guessing.",
      unclaimed:
        'No box claimed this — the only kind of failure the box would have reported was unreadable on the row before any box ran it.',
      ran: 'The box’s failure report did not parse, so we cannot say what went wrong and will not spend another paid run guessing.',
    },
  },
  /**
   * The box reached the model and the model answered, but the answer was
   * prose instead of work — the task's own work never started. The box
   * finished but produced no usable work — no commit, no PR, no diff.
   * Distinct from {@link RunnerError} because no throw fires and no provider
   * 5xx lands: the run ended cleanly with empty output. The cause is a model
   * decision (a misroute, a refusal, a context collapse), not a fault, and the
   * budget reflects that — CHARGED with a finite `maxAttempts` and `Fail` on
   * exhaustion, so an empty-producing model spends the task's ladder like any
   * other fix attempt instead of re-entering forever on a customer's BYOK key.
   *
   * THREE MOMENTS ONE MODEL CAN FINISH HAVING WRITTEN NOTHING, and the third joined
   * them in #4181: the team coordinator submitting a manifest that fails the schema,
   * a babysit follow-up whose only product is a commit over an unchanged tree, and
   * the phantom-edit guard refusing a commit because the tree does not back the paths
   * the manifest DECLARES (41 events / 34 tasks in the 30 days to 2026-09-16, every
   * one previously `runner_error`). The third is the second half of the first: same
   * coordinator, same lane, one step later, and splitting them across this kind and
   * the residual sent an operator to the box for half of one fault. A coordinator
   * that measured the repo and DISPROVED the request submits `noWork` and reaches
   * none of the three. {@link TASK_FAILURE_PRODUCER} holds the enumeration.
   *
   * The documented example: `z-ai/glm-5.3-flash`'s "Thank you for participating
   * in the Stealth Ox Alpha testing period" reply — when it arrives as a 200
   * completion with no work behind it. The same sentence served as a REFUSAL (an
   * error the endpoint raised instead of a code, the shape all 555 prod rows took)
   * is {@link ProviderProseAnswer}. Surfaced as a counted,
   * queryable kind so a maintainer can move the model off the lane's approved
   * list; the auto-demotion half is NOT here and never will be (free-text
   * matching on provider replies is the heuristic {@link classifyTaskFailure}
   * refuses by design, and a wrong match benches a good model fleet-wide).
   *
   * Uncharged would re-queue forever on a `valid` credential (#2567): the pacer
   * that makes `Uncharged` safe for `provider_rate_limit` is the
   * `no_usable_keys` hold, and that hold cannot fire while the key is `valid`
   * (the whole premise of the lane gate). Budgeted as `Charged` with the
   * standard {@link TASK_MAX_ATTEMPTS} ladder and `Fail` on exhaustion —
   * `Uncharged` would re-queue on a key the holder knows is `valid`, and the
   * {@link TaskHoldReason.NoUsableKeys} pacer that makes `Uncharged` safe for
   * {@link ProviderRateLimit} cannot fire here (the credential is valid — that
   * is the premise of issue #2567).
   *
   * Produced by the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}).
   *
   * Producer:
   * 347 rows in the two days to 2026-08-29 — the withdrawn-model prose reply
   * (279) and the coordinator answering with no file manifest (68) — and the
   * kind that names them has been unreachable since it shipped. Two producers,
   * at the two moments a model can finish having written nothing: the team
   * coordinator submitting no file manifest, and a babysit follow-up whose only
   * product is a commit coming back with an unchanged tree.
   * `apps/worker/src/fleet-alerts/task-lifecycle.ts` cited THIS table as the
   * enumeration of both while this list named one — the cross-file citation was
   * right and the table was wrong, which is the direction that is hardest to
   * notice.
   * Beside `apps/runner/src/modes/worker/team/session/errors.ts#TeamResumeNoWorkError`: A RESUMED team attempt (the task's saved commit) whose coordinator answered `no_work` over
   * the prior attempt's red diff — the rule-12 trap, refused and charged like the empty
   * manifest (the team coordinator submitting no file manifest).
   * Beside `apps/runner/src/modes/worker/report.ts#ReportNotProducedError`: A `report` / `ops_action` run that left no REPORT.md, no final turn of prose and
   * no artifact (#3553 F2) — the same definition, on the lane whose work IS prose.
   * Beside `apps/runner/src/modes/worker/team/guard.ts#PHANTOM_EDIT_WIRE_CODE`: #4181: the phantom-edit guard, one step AFTER the no-file-manifest arm. The
   * coordinator's manifest parsed, and the working tree does not back the paths it
   * declares — the editors narrated work nobody wrote, the commit is refused, and
   * the run ends with no diff. Measured: 41 uncoded `error` events over 34 tasks in
   * the 30 days to 2026-09-16, last 2026-09-14, every one filed `runner_error`
   * beside adapter timeouts and OOM kills while its own sibling one step earlier (the
   * no-file-manifest arm) was already named here.
   *
   * ANCHORED ON THE CONSTANT, and TWO throw sites stamp through it — the
   * `apps/runner/src/worktree-bare-store.ts#GIT_AUTH_CODE` shape, for the same reason: `guard.ts`'s
   * `PhantomEditError` and `guard-repair.ts`'s `GuardRepairRefusedError` end the run
   * in the SAME place (a commit refused over a manifest the tree does not back), so
   * one ending must not be able to grade two ways. The reverse scan in
   * `task.test.ts` cannot see either stamp — it matches `code: '<literal>'` and both
   * read the constant — which is exactly the hole a hand-maintained list covers.
   *
   * Budget:
   * #2567: CHARGED with a finite ladder and `Fail` on exhaustion. The
   * credential is valid (issue #2567 premise — a `valid` key answered prose
   * instead of work) so the `no_usable_keys` hold that makes `Uncharged` safe
   * for `provider_rate_limit` cannot fire here. `Uncharged` here is the
   * unbounded re-queue loop on a customer's BYOK credential that this issue
   * exists to prevent. A finite `maxAttempts` (the standard ladder) bounds a
   * hot loop on a customer's BYOK credential without benching the model
   * fleet-wide — that move is the maintainer's, made off a counted kind.
   *
   * Babysit round:
   * The model answered and produced no usable work.
   *
   * Box-at-fault:
   * A model decision, not a fault. `run_produced_nothing` already covers it.
   *
   * Alert sentence:
   * Two producers, and naming one would hide the other: the babysit follow-up
   * grade (`apps/runner/src/modes/worker/babysit.ts` `gradeFollowupLanding`, #2719)
   * for a follow-up whose only product is a commit and whose tree came back
   * unchanged, and the team lane's `ManifestRejectedError` for a coordinator that
   * submitted no file manifest. `TASK_FAILURE_PRODUCER` carries the enumeration.
   *
   * Reason sentence:
   * This row's copy of the MODEL-OUTCOME sentence family (read by `failureReasonSentence`, `apps/api/src/routes/tasks.ts`): the box reached the
   * model and did its job, and what failed is what the model produced.
   * `model_produced_no_work` moved here verbatim when #2235 added the second kind.
   */
  ModelProducedNoWork: {
    value: 'model_produced_no_work',
    ordinal: 15,
    producer: wire(
      'apps/runner/src/modes/worker/team/manifest/collect.ts#ManifestRejectedError',
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamResumeNoWorkError',
      'apps/runner/src/modes/worker/babysit/land.ts#FollowupLandedNothingError',
      'apps/runner/src/modes/worker/report.ts#ReportNotProducedError',
      'apps/runner/src/modes/worker/team/guard.ts#PHANTOM_EDIT_WIRE_CODE',
      'apps/runner/src/modes/scout/brain/run.ts#ScoutRunFailedError',
    ),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: true,
    failureRateExcluded: false,
    copy: {
      alert:
        'The model answered with prose instead of work — no commit, no diff, no pull request. Nothing broke; consider moving this lane off that model.',
      unclaimed:
        'No box claimed this — the model lane that would have run it was unfit before the work started.',
      ran: 'The model answered with prose instead of work — no commit, no diff, no pull request. Nothing broke; consider moving this lane off that model.',
    },
  },
  /**
   * THE SURVEY ANSWERED, AND THE ANSWER WAS LOST ON THE FLOOR (#3400). The
   * grounding pass returned — turns counted, an answer submitted or prose
   * recovered — and the surveyor's OWN post-pass handling threw before the
   * submission could reach `collectManifest`: the JSON-object fallback scan,
   * the scrub, or the non-answer diagnosis. Split out of {@link RunnerError}
   * because that is where this throw filed while it was anonymous: the box
   * reached the model, the model answered, and what broke was OUR code holding
   * the answer — the run timeline shows `grounding_pass_finished
   * {answered: true}` and then nothing, the inside view of the success-shaped
   * silence reported on #3400 (ui-debugger-mcp, 2026-09-11): dispatch reached,
   * no pull request, and no way from outside to say why.
   *
   * Distinct from {@link ModelProducedNoWork} (the coordinator's submissions
   * were COLLECTED and refused — the model's answer was not work; here the
   * answer never got graded at all) and from {@link ManifestGradingFailed}
   * (the submission arrived and the grading of it threw; here the throw is
   * upstream of grading). Budgeted identically to {@link RunnerError}: the
   * split changes the LABEL, never the spend.
   *
   * Producer:
   * #3400 — the three kinds that make the survey-to-working span SELF-NAMING.
   * Between `runGroundingPass` returning and the `working` rung, every throw
   * used to reach `loop.ts` with no `err.code` and file as the residual; the
   * measured case (task `tsk_72035571a9354d47b855fca30e2cc86b`, 2026-09-11)
   * showed `grounding_pass_finished { answered: true }` and then nothing — no
   * `team_manifest`, no `no_work_settled`, no second `run_stage` — and a final
   * row reading `completed` / `runner_error`. ONE producer each, because each
   * kind names exactly one seam of that span; the throw site was deliberately
   * NOT guessed at, so each seam now names itself the next time it fires.
   *
   * Budget:
   * #3400: the three survey-to-working span kinds take rows that were
   * `runner_error` yesterday, so all three are budgeted IDENTICALLY to it —
   * `Charged`, the standard ladder, `Fail` on exhaustion. A re-partition that
   * also re-budgets moves retry behaviour under cover of a naming change, and
   * afterwards nobody can say which half of the diff moved it (the same
   * discipline the 2026-08-29 re-partition states in `VerifyFailed`'s budget, `./tree.ts`). Whether
   * `manifest_event_failed` deserves the `GateUnrunnable` posture (a transport
   * fault is transient across boxes) is an argument for the counted population
   * these kinds exist to produce, not for day one.
   *
   * THE EXHAUSTION ARM FOLLOWS `runner_error` WHEREVER IT GOES, and that is the
   * whole point of the parity rather than a copy of a literal: every throw filed
   * here was a `runner_error` row the day before this kind existed, so an arm that
   * differed would make the RELABEL change the spend for exactly that population —
   * the one thing this split promises it does not do.
   *
   * Babysit round:
   * Old table group: the platform's own code, or the repository moving under the round.
   * Our code threw holding the survey's answer (#3400) — not a babysit producer.
   *
   * Box-at-fault:
   * #3400 — the survey-to-working span kinds, through `ManifestEventFailed`. Each
   * is a box-SIDE throw (the post-pass handler, the manifest's own grading, the
   * audit-event publish), but none is box HEALTH: the survey's work completed,
   * the machine kept running, and the same box serves the next task fine. Paging
   * the box for a named harness fault would double-page what `task_failed`
   * already surfaces.
   *
   * Alert sentence:
   * #3400 — the survey-to-working span, made self-naming. In all three the survey's work
   * was already done and the throw was box-side; the sentences name what was lost and
   * never claim the diff is bad.
   *
   * Reason sentence:
   * #3400 — the survey-to-working span, made self-naming. All three kinds are
   * box-side faults with the survey’s work already done, so the sentences name
   * WHAT was lost (an answered survey, a held submission, a graded manifest)
   * rather than claiming anything about the diff.
   */
  SurveyAnswerLost: {
    value: 'survey_answer_lost',
    ordinal: 16,
    producer: wire('apps/runner/src/modes/worker/team/native.ts#SurveyAnswerLostError'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The survey answered and the answer was lost on the floor — the post-survey handling of a finished grounding pass threw before a manifest could be collected. Nothing here says the change is wrong.',
      unclaimed:
        'No box claimed this — the survey lane that would have run it failed before any box started.',
      ran: 'The box’s survey answered and the answer was lost on the floor — the post-survey handling of a finished grounding pass threw before a manifest could be collected. Nothing here says the change is wrong.',
    },
  },
  /**
   * `collectManifest` HELD A SUBMISSION AND ITS OWN EVALUATION THREW (#3400) —
   * `gradeSubmission`, `trimManifestProse`, the schema parse, or the issue
   * renderers, all of which are written total and none of which carried a code
   * when they threw anyway. The error names WHICH step, so the next occurrence
   * files under its cause instead of the {@link RunnerError} residual.
   *
   * The two callback seams (`onTrimmed`, `onCorrected`) are NOT this kind and
   * never reach it: their contract is "may not throw — recording a trim must
   * not cost the run the trim just saved", and `collectManifest` now ENFORCES
   * that contract by catching, exactly as every status emit on the lane is
   * caught. A throw that can no longer happen needs no name.
   *
   * Distinct from {@link ModelProducedNoWork} — that kind is the ladder's own
   * verdict, thrown deliberately after the last correction; this one is the
   * ladder failing to reach a verdict. Budgeted identically to
   * {@link RunnerError}: the split changes the LABEL, never the spend.
   *
   * Babysit round:
   * Our manifest evaluation threw (#3400) — not a babysit producer.
   *
   * Box-at-fault:
   * #3400 — the survey-to-working span kinds, through `ManifestEventFailed`. Each
   * is a box-SIDE throw (the post-pass handler, the manifest's own grading, the
   * audit-event publish), but none is box HEALTH: the survey's work completed,
   * the machine kept running, and the same box serves the next task fine. Paging
   * the box for a named harness fault would double-page what `task_failed`
   * already surfaces.
   *
   * Alert sentence:
   * #3400 — the survey-to-working span, made self-naming. In all three the survey's work
   * was already done and the throw was box-side; the sentences name what was lost and
   * never claim the diff is bad.
   */
  ManifestGradingFailed: {
    value: 'manifest_grading_failed',
    ordinal: 17,
    producer: wire('apps/runner/src/modes/worker/team/manifest/collect.ts#ManifestGradingError'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The box held the survey’s submission and its own evaluation of it threw — grading, trimming, or parsing the manifest. The error names which step; nothing here says the change is wrong.',
      unclaimed:
        'No box claimed this — the manifest grading that would have run it failed before any box started.',
      ran: 'The box held the survey’s submission and its own evaluation of it threw — grading, trimming, or parsing the manifest. The error names which step; nothing here says the change is wrong.',
    },
  },
  /**
   * THE MANIFEST EXISTED AND A PUBLISH CARRYING IT COULD NOT BE MADE (#3400).
   * THREE publishes at the survey/working seam ride this one name, and the fact
   * is the same for all three — the manifest was collected, and the record of
   * it is what is missing:
   *   - `team_manifest`, the audit event;
   *   - the rule-12 no-work settlement, whose loss would hand the mothership a
   *     completed run with no record that the refusal was deliberate;
   *   - the `working` rung's ledger entry, the record a resumed attempt reads
   *     to find the work.
   * The first goes through `observedEmit`, which ends in a raw
   * `await emit(...)` with no `.catch(...)`, unlike every sibling emit on the
   * lane; the other two are bare `await`s of their own seams beside it. A
   * throw at any of them (the fleet transport refusing the publish, or the
   * payload build itself) propagated to `loop.ts` carrying no code and filed
   * as the {@link RunnerError} residual — with the run timeline showing a
   * finished, answered survey and then silence, the shape found on task
   * `tsk_72035571a9354d47b855fca30e2cc86b` (ui-debugger-mcp, 2026-09-11).
   * #3400 is where that day's silence was reported from outside — dispatch
   * reached, no pull request, no way to say why — against a DIFFERENT row of
   * the same repo; this is the row the fix was measured on.
   *
   * Every one of them still FAILS the run — swallowing one would let a run
   * continue with its manifest declaration lost, and the declaration is what
   * the working rung parks in the ledger — but it now fails NAMED. One kind,
   * not three, because the operator move is the same for all three publishes
   * (look at the transport, never at the diff) and a vocabulary nobody can
   * query usefully is the failure this vocabulary exists to avoid. Distinct from
   * {@link ManifestGradingFailed} (the manifest was already accepted there;
   * here the fault is the publish) and from {@link ModelProducedNoWork} (there
   * is no manifest at all). Budgeted identically to {@link RunnerError}: the
   * split changes the LABEL, never the spend.
   *
   * Babysit round:
   * A publish carrying the manifest failed (#3400) — not a babysit producer.
   *
   * Box-at-fault:
   * #3400 — the survey-to-working span kinds, through `ManifestEventFailed`. Each
   * is a box-SIDE throw (the post-pass handler, the manifest's own grading, the
   * audit-event publish), but none is box HEALTH: the survey's work completed,
   * the machine kept running, and the same box serves the next task fine. Paging
   * the box for a named harness fault would double-page what `task_failed`
   * already surfaces.
   *
   * Alert sentence:
   * #3400 — the survey-to-working span, made self-naming. In all three the survey's work
   * was already done and the throw was box-side; the sentences name what was lost and
   * never claim the diff is bad.
   */
  ManifestEventFailed: {
    value: 'manifest_event_failed',
    ordinal: 18,
    producer: wire('apps/runner/src/modes/worker/team/session/errors.ts#ManifestEventError'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The run surveyed and graded a manifest, and publishing its audit event is what failed — the audit record of finished work is what is missing. Nothing here says the change is wrong.',
      unclaimed: 'No box claimed this — the run’s event pipeline failed before any box started.',
      ran: 'The run surveyed and graded a manifest, and publishing its audit event is what failed — the audit record of finished work is what is missing. Nothing here says the change is wrong.',
    },
  },
  /**
   * THE RUN-START SERVICE PROBE'S ROW COULD NOT BE PUBLISHED (plan 2026-10-01 row
   * 09-44) — the probe's evidence row is what is missing (the record #4333's advert
   * retraction would read, once built; today nothing consumes it, row 09-49). The {@link ManifestEventFailed} class one stage earlier: the row is
   * built through `hivePayload`, which throws in strict mode, and the publish itself can
   * fault, so the stage wraps both and throws
   * `apps/runner/src/modes/worker/team/session/errors.ts#TeamServiceProbeEventError`
   * with this code. The code had no kind, so it graded {@link RunnerError}: the same
   * spend, filed beside adapter timeouts and OOM kills and blamed on the box.
   *
   * Its own kind rather than a second producer of `manifest_event_failed`: that name
   * says a manifest existed, and here no survey has run. Budgeted identically to both:
   * the split changes the LABEL, never the spend.
   *
   * Babysit round:
   * A publish at run start failed — not a babysit producer.
   *
   * Box-at-fault:
   * A box-SIDE throw that is not box HEALTH, as for the #3400 span kinds: the probe ran,
   * the machine kept running, and the same box serves the next task fine.
   *
   * Alert sentence:
   * Names what was lost (the probe's record) and never claims the change is wrong —
   * no change exists yet.
   */
  ServiceProbeEventFailed: {
    value: 'service_probe_event_failed',
    ordinal: 54,
    producer: wire(
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamServiceProbeEventError',
    ),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The run probed the services the repo declares, and publishing the record of that probe is what failed — the run stopped before any work started. Nothing here says the change is wrong.',
      unclaimed:
        'No box claimed this — the failure recorded is the run-start service probe: the record of probing the services the repo declares could not be published. Nothing here says the change is wrong.',
      ran: 'The run probed the services the repo declares, and publishing the record of that probe is what failed — the run stopped before any work started. Nothing here says the change is wrong.',
    },
  },
  /**
   * A BOX-SIDE bound fired and aborted the run, because the operator move is the
   * same for each — raise the bound, or find out why the step will not finish
   * inside it. The live producers ({@link TASK_FAILURE_PRODUCER}):
   *   - the no-progress stall guard (`no new tool call or output for 300000ms
   *     (last: code.read)`) and the startup deadline, both in
   *     `apps/runner/src/guards.ts`,
   *   - a provider stream silent past its idle bound (`ProviderStreamIdleError`,
   *     `apps/runner/src/llm/streamed-generate.ts`, #5819) — the only per-request
   *     bound on the streamed wire since #5893,
   *   - the team lane's verify gate answering no verdict
   *     (`modes/worker/team/session/errors.ts`).
   * They are not split by producer: the message already names WHICH bound was
   * hit, and members with one-digit populations are vocabulary nobody can query
   * usefully.
   *
   * TWO ENTRIES THIS LIST USED TO CARRY ARE GONE, and it named them long after
   * they stopped existing — the defect this repo calls "a comment asserting a
   * guarantee the code no longer provides", and the reason a debugger spent
   * 2026-09-20 hunting a configured ceiling that had been deleted for 17 days:
   *   - `the wall-clock runtime ceiling (runtime ceiling exceeded: 3600000ms)` —
   *     `runtimeCeiling`, DELETED 2026-09-03 with `DZ_RUN_CEILING_MS` (owner's
   *     ruling; `apps/runner/src/guards.ts`'s header has the measurement). It is
   *     also now unbuildable: `scripts/agent-budgets.guard.test.ts` bans the shape
   *     by name. Prod agrees — the `ceiling` guard last tripped 2026-09-03
   *     18:01:10 and has not fired since, against 110 trips in the 13 days before.
   *   - `the per-LLM-step deadline the coding lane configures` — `withDeadline`
   *     had no producer on this path and the coding lane configures none.
   * THE LOOP DETECTOR IS ALSO NOT HERE, as of #4556: it grades
   * {@link RunLooping}, because a repetition hits no wall at all.
   *
   * THE PER-REQUEST BUDGET WAS THIS DOCBLOCK'S OWN DEFERRED SUSPICION, and closing it (#4181)
   * corrected the suspicion as well as the gap. The paragraph used to end here by
   * naming 11 prod rows reading exactly `The operation was aborted.` with NO
   * `guard_tripped` event anywhere in the run — spread across
   * `native_harness_failed`, `grounding_pass_failed` and the review lane — and
   * pointing at `resolveRequestTimeoutMs` (`apps/runner/src/llm/request-timeout.ts`)
   * as the suspected escape. That file was the right place to look and the wrong
   * population to expect there: its throw said `inference request abandoned after
   * …`, never `The operation was aborted.`, and prod carried ZERO uncoded events
   * matching it in the 30 days to 2026-09-16 — the 90-minute cap never ended a
   * run. That throw site was a declared producer until #5893 moved structured output to
   * the streamed wire and removed it; its class and its producer entry are deleted, and a
   * silent stream now grades here through `ProviderStreamIdleError`. The 11
   * aborted rows are STILL unexplained and still `runner_error`. Giving them this
   * kind would be a guess about which bound they hit. Saying so here beats a map
   * that appears to cover them and does not.
   *
   * Distinct from {@link ProviderError}, and the border is which timer fired. #4021
   * grades a transport timeout — a platform timer, or a fetch that gave up, before any
   * bound of ours (stall guard, inference hold, stream idle bound) fired — as the
   * endpoint's own fault, remedy WAIT. This kind is OUR bound firing, and the remedy is ours.
   *
   * Distinct from {@link StuckSilent}, and the distinction is WHICH SEAM
   * NOTICED. This is the box reporting its own ceiling within seconds of the
   * trip, naming the wall it hit and the last thing it was doing; `stuck_silent`
   * is the mothership reaper inferring silence up to 180 minutes later with
   * nothing to name. Collapsing them into one kind throws away the only two
   * facts a debugger starts from.
   *
   * Budgeted identically to {@link RunnerError}.
   *
   * Babysit round:
   * A box-side bound fired on a running round — a hung step the work started, or a slow model. Mixed.
   *
   * Box-at-fault:
   * A ceiling tripped. The domain states the operator move itself: raise the
   * bound, or find out why the step will not finish inside it. Neither is a
   * box-health move, and a long-but-healthy run would page every time.
   */
  RunTimeout: {
    value: 'run_timeout',
    ordinal: 22,
    producer: wire(
      'apps/runner/src/guards.ts#GuardAbortError',
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamVerifyNoVerdictError',
      // #5819: a provider stream idle past its bound is our per-step deadline firing.
      'apps/runner/src/llm/streamed-generate.ts#ProviderStreamIdleError',
    ),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The run hit a wall-clock, no-progress, or per-step deadline on the box and was aborted. The run timeline names which one.',
      unclaimed:
        'No box claimed this — the run window would have elapsed before any box could start.',
      ran: 'The run hit a wall-clock, no-progress, or per-step deadline on the box and was aborted. The run timeline names which one.',
    },
  },
  /**
   * The run kept TALKING and stopped GETTING ANYWHERE — turns advanced, the tree
   * did not. Distinct from every other kind here because nothing was wrong: no
   * bound fired, no tool failed, no gate refused. The model simply re-asked a
   * question it already had the answer to, forever.
   *
   * MEASURED, 2026-09-19, `tsk_d336ae463bbff52f94d2477d034dc239` on tesote/tesote.ai:
   * 1023 `native_harness_turn` events, each carrying exactly one tool call and
   * `output_tokens: 114` — byte-identical turn after turn — with `input_tokens`
   * climbing exactly 282 per turn as one constant-size tool result was appended
   * again and again. It was polling a background command it had ALREADY read to the
   * end; its own last words were "already in hand (read at cursor 0 and cursor 84;
   * ends there)". Two hours, no diff, no PR, cancelled by hand.
   *
   * WHY THE GUARDS THAT EXIST COULD NOT SEE IT, and why this kind is not
   * {@link RunTimeout}. `stallDetector` watches wall-clock silence since the last
   * NOVEL observation, and its fingerprint includes the model's own prose — a
   * sentence reworded is novelty, so the 5-minute clock re-armed forever.
   * `loopDetector` watches repeats of a tool-call SIGNATURE over the call's whole
   * argument object, and a poller that walks `cursor` or jitters `wait_ms` mints a
   * fresh identity every call, so its window never fills. Both were armed on that
   * run (`loop_repeats: 10`, `stall_ms: 300000`) and neither could fire. Naming it
   * `run_timeout` would name a wall that was never hit — the same mis-naming #4503
   * existed to end.
   *
   * The seam that DOES see it watches tool RESULTS: the same tool answering the
   * same bytes, over and over, while nothing mutates the tree
   * (`apps/runner/src/harness/futility.ts`).
   *
   * Budgeted like {@link RunTimeout} — charged, the full ladder, `fail` on
   * exhaustion. A redelivery can genuinely fix it: the loop is a property of one
   * model's one trajectory, not of the box and not of the ask.
   *
   * Producer:
   * The futility latch: turns advancing, tree unchanged. It reaches the wire through
   * the SAME `guards.end()` door #4503 opened for the gate latch, so the abort, the
   * audit event and first-trip-wins all behave identically. The symbol named here is
   * the function that decides and calls `end` — `GuardAbortError` carries the code, so
   * anchoring on the error class would name the messenger for four different kinds.
   * Beside `apps/runner/src/harness/futility.ts#invalidCallVerdict`: The same latch counting calls the SDK refused before any tool ran (`recordInvalid`).
   *
   * Budget:
   * Identical to `RunTimeout` and for the same reason: a futile trajectory is one
   * model's one run, not a fact about the box or the ask, so a redelivery has a real
   * chance. NOT box-local (`taskFailureBoxLocal`) — nothing about the box produced it,
   * and excluding the box would shrink the pool for no reason.
   *
   * Babysit round:
   * The run kept talking and stopped progressing.
   *
   * Box-at-fault:
   * NOT the box's fault, and the distinction is the whole point of paging: the box
   * did everything asked of it: it ran the tools and returned honest answers. The
   * model is what went in circles, so provisioning this box changes nothing. Same
   * side, same reason, for `RunLooping`: the box ran every tool it was asked to and
   * answered honestly; the model repeated itself.
   *
   * Alert sentence:
   * THE RUN WAS ALIVE AND USELESS — the two kinds where nothing broke. Both say so before
   * anything else, because the operator's first question is "was something broken?" and for
   * both the answer is no: the box ran every tool it was asked to and answered honestly.
   *
   * They are a family, not a synonym. `run_futile` is 25 consecutive identical answers with
   * the tree UNMUTATED throughout; `run_looping` is one tool signature repeating with
   * identical answers inside a sliding window, and says nothing about the tree. The
   * sentences differ where that difference reaches the reader: only the futile one can
   * promise the checkout never changed.
   */
  RunFutile: {
    value: 'run_futile',
    ordinal: 23,
    producer: wire(
      'apps/runner/src/harness/futility.ts#futilityVerdict',
      'apps/runner/src/harness/futility.ts#invalidCallVerdict',
    ),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The run kept taking turns and stopped getting anywhere — the same tool answered the same bytes over and over while nothing in the checkout changed, so it was ended instead of billed. Nothing failed: no bound fired and no tool errored. Re-dispatch is reasonable; a futile trajectory belongs to one run, not to the box or the ask.',
      unclaimed: 'No box claimed this — nothing ran, so nothing could be futile.',
      ran: 'The run kept taking turns and stopped getting anywhere — the same tool answered the same bytes over and over while nothing in the checkout changed, so it was ended instead of billed. Nothing failed: no bound fired and no tool errored. Re-dispatch is reasonable; a futile trajectory belongs to one run, not to the box or the ask.',
    },
  },
  /**
   * The run made the SAME TOOL CALL, got the SAME ANSWER, over and over — the loop
   * detector's own verdict (`apps/runner/src/guards.ts` {@link loopDetector}, #4556).
   * Nothing was waited for and nothing expired, which is exactly why this is not
   * {@link RunTimeout}: naming a trip after a wall that was never hit is the mis-naming
   * {@link RunFutile} already refuses.
   *
   * WHY IT NEEDED ITS OWN KIND, measured. Until 2026-09-20 every loop trip settled as
   * `run_timeout`, so `tasks.failure_kind` said "a bound fired" for a run no bound
   * touched. In the 30 days to that date there were 27 loop trips and **23 of them were
   * `code.bash_output`** — a model blocking on a live background command with the
   * platform's own `wait_ms` contract, killed for waiting. Establishing that required
   * joining `tasks.failure_kind` to `task_run_events.payload->>'guard'`, because the task
   * row carried no discriminator; a `GROUP BY failure_kind` saw nothing. The guard kind
   * was always persisted — this kind is the PROJECTION of it that the tasks table lacked,
   * not a new classification.
   *
   * THE FALSE POSITIVES THAT MOTIVATED IT ARE FIXED IN THE SAME CHANGE, so this kind
   * should now be rare and should mean what it says: the detector consults the tool's
   * ANSWER, and a repeat whose answer changed clears its streak. A trip that survives
   * that is a model re-asking a question it already has the answer to.
   *
   * Distinct from {@link RunFutile}, and the border is what each one proves. This is
   * `repeats` identical answers to ONE signature inside a sliding window of `repeats * 2`
   * tool calls, and it says nothing about the tree. `run_futile` is 25 CONSECUTIVE
   * identical answers with the tree UNMUTATED throughout — a stricter, slower predicate
   * that catches the cycle this one is structurally blind to (three or more alternating
   * signatures can never fill a window of `repeats * 2`). Both stay armed: neither
   * subsumes the other, and a run that mutates files between identical polls is this
   * kind's alone.
   *
   * Budgeted like {@link RunFutile} — charged, the full ladder, `fail` on exhaustion. A
   * redelivery can genuinely fix it: a repetition loop belongs to one model's one
   * trajectory, not to the box and not to the ask.
   *
   * Producer:
   * The loop detector, stamping its own trip instead of defaulting to `run_timeout`
   * (#4556). The symbol is the detector that decides and calls `fire` — `GuardAbortError`
   * carries the code onto the wire, so anchoring on the error class would name the
   * messenger for three different kinds.
   *
   * Budget:
   * Identical to `run_futile`, for the identical reason: a repetition loop is one model's
   * one trajectory, so a redelivery has a real chance. NOT box-local — nothing about the
   * box produced it, and excluding the box would shrink the pool for no reason.
   *
   * Babysit round:
   * The run repeated the same call and answer.
   *
   * Box-at-fault:
   * NOT the box's fault, and the distinction is the whole point of paging: the box
   * did everything asked of it: it ran the tools and returned honest answers. The
   * model is what went in circles, so provisioning this box changes nothing. Same
   * side, same reason, for `RunLooping`: the box ran every tool it was asked to and
   * answered honestly; the model repeated itself.
   *
   * Alert sentence:
   * THE RUN WAS ALIVE AND USELESS — the two kinds where nothing broke. Both say so before
   * anything else, because the operator's first question is "was something broken?" and for
   * both the answer is no: the box ran every tool it was asked to and answered honestly.
   *
   * They are a family, not a synonym. `run_futile` is 25 consecutive identical answers with
   * the tree UNMUTATED throughout; `run_looping` is one tool signature repeating with
   * identical answers inside a sliding window, and says nothing about the tree. The
   * sentences differ where that difference reaches the reader: only the futile one can
   * promise the checkout never changed.
   */
  RunLooping: {
    value: 'run_looping',
    ordinal: 24,
    producer: wire('apps/runner/src/guards/loop.ts#loopDetector'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The run made the same tool call and got the same answer over and over, so it was ended rather than left to repeat. No bound fired and no tool errored — this is not a timeout. Re-dispatch is reasonable; a repetition loop belongs to one run, not to the box or the ask.',
      unclaimed: 'No box claimed this — nothing ran, so nothing could repeat.',
      ran: 'The run made the same tool call and got the same answer over and over, so it was ended rather than left to repeat. No bound fired and no tool errored — this is not a timeout. Re-dispatch is reasonable; a repetition loop belongs to one run, not to the box or the ask.',
    },
  },
  /**
   * A signal KILLED the run — it did not crash and it did not finish. A box
   * drain, a rolling deploy, an operator stop, an OOM kill: the process was
   * terminated from outside, so there is no diff, no stack and nothing on the
   * box to diagnose.
   *
   * It was invisible by construction until 2026-08-29. `runProgram` computed
   * `exitCode: proc.exitCode ?? 1` while already awaiting the wait status, so
   * EVERY signal kill reported a flat exit 1 and folded into the same bucket as
   * a genuine non-zero exit — 445 `exited 1` rows in two days with the kills
   * indistinguishable inside them. `ProgramResult.killedBy` is the fact that
   * makes this kind producible.
   *
   * PRECEDENCE, because one signal has two causes: a wall-clock or stall guard
   * aborts by SIGTERM too, so a producer checks the guard's `tripped()` FIRST
   * and stamps {@link RunTimeout} when it fired. `run_killed` is the residual —
   * killed, and not by one of our own ceilings. Without that order the two kinds
   * would race for the same signal and neither would mean anything.
   *
   * Budgeted identically to {@link RunnerError} today. There is a real argument
   * for {@link TaskBudgetKind.Uncharged} — a rolling fleet deploy drains every
   * box at once and burns one attempt on every in-flight task through no fault
   * of the task's — and it needs the counted population this kind exists to
   * produce before it is more than an argument.
   *
   * Producer:
   * The abort that reaches the BYOK wire without a guard behind it. Precedence
   * over `run_timeout` is STRUCTURAL, not ordered-by-luck: a guard's own
   * `GuardAbortError` is re-raised verbatim one branch earlier, so a ceiling can
   * never read as a kill.
   * THREE producers, one residual. The inference wire's abort; the team lane's
   * verify gate taken by a signal NOTHING attributed (a gate the OOM killer took at a
   * named bound no longer reaches this kind — it is `GateMemoryUnmet` since #6347,
   * uncharged, because a bound that fired is the box's fact; the whole-graph gate that
   * thrashed sebastian-3 off the network on 2026-08-28 is the recorded case); and a
   * team session stopped by a signal where NO wall named itself (`teamAbortCause`
   * hands a named wall's own error back instead, so `DeadlineError` and
   * `GuardAbortError` never reach here).
   *
   * Babysit round:
   * A signal killed the run. Mixed: a drain, deploy or operator stop is not the work's
   * fault, but an OOM kill is one of its causes (`../task/failure-kind.ts`), and a fix
   * whose tests blow the box's memory is. The kind cannot tell which, so it charges.
   *
   * Box-at-fault:
   * A signal killed the run from OUTSIDE. Three of its four causes are things
   * we did on purpose (a drain, a rolling deploy, an operator stop), and a
   * rolling deploy drains every box at once — blaming the box would page the
   * entire fleet for a deploy we started, which is the ignored-page generator.
   * The fourth, an OOM kill, is box capacity and has its own `resource` kind.
   */
  RunKilled: {
    value: 'run_killed',
    ordinal: 25,
    producer: wire(
      'packages/agent/src/providers/grade.ts#RequestAbortedError',
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamVerifyNoVerdictError',
      'apps/runner/src/modes/worker/team/session/errors.ts#TeamAbortedError',
    ),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'We stopped this run — a signal killed it from outside (a box drain, a rolling deploy, an operator stop, or an out-of-memory kill). There is nothing on the box to diagnose.',
      unclaimed:
        'No box claimed this — the task was stopped before any box could start. There is nothing on the box to diagnose.',
      ran: 'We stopped this run — a signal killed it from outside (a box drain, a rolling deploy, an operator stop, or an out-of-memory kill). There is nothing on the box to diagnose.',
    },
  },
  /**
   * THE SCOUT'S SURVEY STAGE RAN AND ZERO FINDINGS CAME BACK, so the plan call
   * had nothing anchored to ground a plan on and declined to invent one. The
   * brain throws rather than minting a blocker, because the missing thing is
   * this run's own survey output and no human answer reaches a live run (#3033);
   * `blocker answer` mints a raw task, it restarts nothing.
   *
   * BUDGETED EXACTLY AS {@link ScoutSurveyUnwired} — `Charged`, the full
   * {@link TASK_MAX_ATTEMPTS} ladder, `Fail` on exhaustion — and the parity is
   * the argument, not a coincidence. Those two kinds are the SAME STAGE failing
   * to produce: `scout_survey_unwired` is the seam absent, this is the seam
   * present and answering nothing. Both are remedied by the same move — re-survey,
   * ideally on another box (#2954) — so a reader who knows one knows the other,
   * and a divergence here would be a claim that the survey's absence and the
   * survey's silence want different retries. They do not.
   *
   * SO THIS MEMBER MOVES NO SPEND, DELIBERATELY. These rows are `runner_error`
   * today, which is `Charged` / 3 / `Fail`, and the new entry is the same three
   * values: the retry was never the defect, the LABEL was. The 2026-08-29
   * re-partition's rule holds — a split that also re-budgets moves retry
   * behaviour under cover of a naming change, and afterwards nobody can say which
   * half of the diff moved it. The win here is readability plus the two sibling
   * kinds landing with it, whose retries WERE waste.
   *
   * NOT `Uncharged`, and the reason is that the run SPENT. The survey rounds ran,
   * the grounding `ask` ran and the plan call ran — all on the customer's BYOK
   * key — before the emptiness was known, so "no fix was produced" (the phrase
   * that earns {@link ProviderRateLimit} its uncharged arm) is false here: a fix
   * was attempted and paid for. And nothing paces an uncharged re-queue on this
   * lane: there is no box cooldown ({@link ProviderRateLimit}'s pacer) and no
   * per-task exclusion, so `Uncharged` would re-run a paid scout survey forever
   * at zero attempts spent.
   *
   * NO BOX-FAULT EXCLUSION, though #2954 reads the empty survey as a box state.
   * That reading is a HYPOTHESIS: the same silence is also what a repo with
   * nothing citable, or a model that declines every scout question, produces, and
   * this platform cannot tell those apart from the counts alone. The exclusion set
   * ({@link BOX_FAULT_EXCLUSION_MINUTES}) takes measured evidence per kind and
   * refuses a ride on a sibling's, so it stays out until a read says which. The
   * read that settles it: group the rows this kind starts producing by
   * `runner_id` — one box carrying the population is the box's fault, an even
   * spread is the repo's or the model's.
   *
   * Produced at `apps/runner/src/modes/scout/brain/run.ts#ScoutSurveyEmptyError`,
   * whose `code` is this value; the scout executor's catch re-raises it and
   * `loop.ts` forwards `err.code` onto the terminal `error` event unchanged, so
   * the wire `code` map ({@link TASK_FAILURE_WIRE_CODE}) grades it by enumeration.
   *
   * Producer:
   * The scout brain's two deliberate throws — the exception to that lane's "a scout
   * returns a valid plan, never a throw" rule, carved because the floor plan is
   * DISPATCHED and would cost a customer a pull request built from an empty
   * envelope. Both stamped `err.code` from the day they landed, per the #2570
   * convention; neither code was in this vocabulary, so `classifyTaskFailure`'s
   * enumeration lookup missed both and every row came back as the residual with the
   * full charged ladder. A STAMP IS NOT A GRADE, and these two entries are the half
   * that was missing.
   *
   * Budget:
   * PARITY WITH `scout_survey_unwired`, which is the sibling and not a template of
   * convenience: both kinds are the scout's SURVEY STAGE failing to produce — that
   * one because the seam is absent, this one because the seam ran and answered
   * nothing — and both are remedied by re-surveying, ideally on another box
   * (#2954). So `Charged`, the full ladder, `Fail` on exhaustion.
   *
   * THESE THREE VALUES ARE ALSO WHAT THESE ROWS ALREADY GET, and that is deliberate.
   * They grade `runner_error` today (an unbudgeted wire code falls to the residual),
   * so naming the kind moves NO spend: the retry was never the defect here, the label
   * was. The 2026-08-29 re-partition's rule, applied: a split that also re-budgets
   * moves retry behaviour under cover of a naming change.
   *
   * NOT `Uncharged` — the run SPENT before the emptiness was knowable. The survey
   * rounds, the grounding `ask` and the plan call all ran on the customer's BYOK key,
   * so "no fix was produced", the phrase that earns `provider_rate_limit` its
   * uncharged arm, is false. And nothing paces an uncharged re-queue on this lane:
   * no box cooldown, no per-task exclusion, so it would re-run a paid scout survey
   * forever at zero attempts spent.
   *
   * Babysit round:
   * The survey returned nothing — a box state or a model declining; a hypothesis, so it charges.
   *
   * Box-at-fault:
   * The three kinds named on 2026-09-19, and all three answer NO — each for its own
   * reason, not as a batch. `PoolKeySpent` is the account's WALLET behind one pool
   * key: it is cooled and out of this task's rotation, so the next attempt runs on
   * a sibling from any box — `ProviderQuotaExhausted`'s posture, reached through the
   * pool door. `MaxOutputUnknown` is the model registry and the assignment's own
   * limits: every box in the fleet reads the same two, so provisioning the refusing
   * box changes nothing. `SurveyEmpty` is the ONE judgement call here, deliberately
   * the same NO: the identical silence is what a repository with nothing citable, or
   * a model that declines every scout question, produces, and the counts cannot
   * tell those apart — paging on that hypothesis is how a box alert stops being
   * read. Held with `taskFailureBoxLocal`'s refusal of the same kind
   * (`BOX_LOCAL_FAILURE_KINDS`), so the two seams state one reading.
   *
   * Alert sentence:
   * The three wire codes named on 2026-09-19. All three throw sites stamped `err.code`
   * from the day they landed and none of the codes was a `TaskFailureKind`, so every one
   * of these rows read as the RESIDUAL's sentence — "the worker box threw and no named
   * cause claimed it" — which was false in the one way that matters to the reader: the
   * box had named the cause precisely.
   *
   * Each sentence therefore does the one thing the residual's could not: it says whose
   * fault it is and what clears it. `survey_empty` is nobody's and re-surveys;
   * `pool_key_spent` is the account's wallet and charges nothing; `max_output_unknown` is
   * this platform's own model configuration and will not clear on a retry.
   *
   * `review_no_model` (plan 101 K4) joined the family: the review lane's refusal to post
   * a verdict no model reached. The row re-pends HELD (`review_awaiting_model`), so its
   * sentence mostly rides the transition trail, and it says what clears it.
   *
   * Reason sentence:
   * The three kinds named on 2026-09-19. Each stamped a wire code from the day its
   * throw landed and none of the codes was in the vocabulary, so every row read as
   * the residual's sentence — "a worker box threw and no named cause claimed it" —
   * while the box had named the cause precisely.
   */
  SurveyEmpty: {
    value: 'survey_empty',
    ordinal: 36,
    producer: wire('apps/runner/src/modes/scout/brain/run.ts#ScoutSurveyEmptyError'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The scout surveyed the repository and came back with zero findings, so the planner had nothing to anchor a plan on and declined to invent one. The ask is not the problem: the row re-surveys on its next attempt, usually on another box. A repository that keeps answering nothing has nothing the scout can cite for this ask.',
      unclaimed:
        'No box claimed this — the scout survey that would have grounded the plan answered nothing before any box started.',
      ran: 'The scout surveyed your repository and came back with zero findings, so the planner had nothing to anchor a plan on and declined to invent one. Nothing here says your ask is wrong: the run re-surveys on its next attempt, usually on another box. If it keeps happening, the repository has nothing the scout can cite for this ask — narrow it, or point it at a path that carries the code.',
    },
  },
  /**
   * THE SCREEN DISCARDED THE TASK'S OWN TEXT (H7 #5444, plan 101 slice 09 "push paths").
   * Before any lane renders the envelope's free text (title, problem, operator notes, the
   * prior conversation) into a model prompt, the box has the mothership screen it once
   * over `fleet.req` `screen_items` (`apps/runner/src/envelope-screen.ts`). A `discard`
   * (injection, abuse, spam, empty) means the ASK itself is what the screen refused, so
   * the run stops before any model reads it.
   *
   * Producer:
   * `apps/runner/src/envelope-screen.ts#TaskTextDiscardedError`, thrown by the run-start
   * hook with the discarded fields named; the per-item verdicts are audited mothership-side
   * (`prompt.item_screened`, one row per item) and ride the run's `task_text_screened`
   * status event (kinds, verdicts, digests — never text).
   *
   * Budget:
   * TERMINAL. A retry re-screens the same text against the same stored verdict and is
   * discarded again.
   *
   * Babysit round:
   * CHARGED. The round never started — the screen refused its brief — but a re-mint rebuilds
   * the brief from the same PR text and meets the same stored verdict, so an excused discard
   * would re-mint until `maxSweeps`. Charged, the ladder spends and escalates instead.
   *
   * Box-at-fault:
   * No. The box did exactly what it must; the text is the subject.
   *
   * Alert sentence:
   * Names the refusal and the remedy: the ask has to be restated by a person.
   *
   * Reason sentence:
   * The same: the task's text was refused as untrusted input, nothing ran.
   */
  ScreenDiscarded: {
    value: 'screen_discarded',
    ordinal: 49,
    producer: wire('apps/runner/src/envelope-screen.ts#TaskTextDiscardedError'),
    budget: terminal(),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: true,
    copy: {
      alert:
        'The intake screen refused this task’s own text (its title, problem, operator notes or earlier conversation) as untrusted input, so no model read it and nothing ran. It will not be retried: restate the ask in a new task.',
      unclaimed:
        'The intake screen refused this task’s text as untrusted input before any model read it, so nothing ran. Restate the ask in a new task.',
      ran: 'The intake screen refused this task’s text (its title, problem, operator notes or earlier conversation) as untrusted input before any model read it, so nothing ran and nothing was written. It is not retried, because the same text would be refused again: restate the ask in a new task.',
    },
  },
  /**
   * THE SCREEN COULD NOT ANSWER FOR THE TASK'S OWN TEXT (H7 #5444). The run-start hook
   * (`apps/runner/src/envelope-screen.ts`) got no verdict for some field: the account has
   * no usable key for the screen, a judgement timed out, the claim fence refused the
   * request, or the worker is too old to know the verb (`NatsReplyError`). Unscreened text
   * is never rendered, so the run stops before any model reads it.
   *
   * Producer:
   * `apps/runner/src/envelope-screen.ts#TaskTextUnavailableError`.
   *
   * Budget:
   * Uncharged, capped at three in a row: nothing ran, so nothing is the task's fault, but a
   * screen that keeps failing on a pool that still serves (a timeout, a fenced request) must
   * end the task rather than re-pend it forever at zero attempts spent. A failure that lands
   * while NO key on the account can serve the screen is not this kind: the mothership
   * regrades it {@link ScreenKeyUnusable}, which waits instead (2026-10-18). Nor is one whose
   * judgement the PROVIDER refused on rate or quota (2026-11-01): the responder grades its
   * throw and the regrade reads it (`apps/worker/src/fleet/screen-fault.ts`). What stays
   * here, capped: a 5xx, a timeout on a key nothing was pacing, an unexplained throw, a
   * fenced or capped request.
   *
   * Park:
   * `provider_capacity`, for `SCREEN_UNAVAILABLE_RECHECK_MS` (2026-10-09: re-pended at once,
   * a review on a key stuck behind a 5-hour usage window was re-claimed every 1-2 s, lease
   * epoch 1 -> 19 in 40 minutes). It parked on `no_screen_key` until PR #6574's review
   * (2026-10-19): since the keyless cause is regraded {@link ScreenKeyUnusable}, what
   * reaches this kind is a screen call that failed on a pool that still serves, and the
   * row read "no screen key" on an account with working keys. The capacity hold says what
   * happened (the provider did not answer, the key is fine) and wakes on the same clock; it
   * gives up the key-valid wake, which only the keyless cause needs and keeps.
   *
   * Babysit round:
   * The round never started: the screen could not judge its brief.
   *
   * Box-at-fault:
   * No. The screen runs on the mothership over the account's own key.
   *
   * Alert sentence:
   * Names the wait and the one durable cause (no usable key for the screen).
   *
   * Reason sentence:
   * The same: nothing ran, nothing was charged, it retries on its own.
   */
  ScreenUnavailable: {
    value: 'screen_unavailable',
    ordinal: 50,
    producer: wire('apps/runner/src/envelope-screen.ts#TaskTextUnavailableError'),
    budget: uncharged(3),
    park: {
      reason: TaskHoldReason.ProviderCapacity,
      backstopMs: SCREEN_UNAVAILABLE_RECHECK_MS,
      honorsStatedReset: false,
    },
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: true,
    copy: {
      alert:
        'The intake screen could not judge this task’s text, so no model read it, nothing ran and no attempt was charged. It retries on its own; three in a row end it. If it keeps happening, check that the account has a working key the screen can use.',
      unclaimed:
        'The intake screen could not judge this task’s text before any model read it, so nothing ran and nothing was charged. It retries on its own.',
      ran: 'The intake screen could not judge this task’s text, so no model read it and no attempt was charged. The task retries on its own and stops after three such failures in a row; if it keeps happening, check that the account has a working key the screen can use.',
    },
  },
  /**
   * THE SCREEN COULD NOT ANSWER, AND NO KEY ON THE ACCOUNT CAN SERVE IT (2026-10-18) — AS
   * THE REGRADE'S OWN UNPINNED READ SEES IT. A box's `screen_unavailable` REGRADED on the
   * mothership when, as the failure lands, the account's pool holds no key the screen's
   * lane could run on — the claim gate's own PREDICATE (`screenKeyServes`,
   * `apps/worker/src/dispatch/screen-key-gate.ts`), which THIS REGRADE resolves with a
   * NULL pin (its `owner` port answers the account only, so it cannot scope a repo:
   * `apps/worker/src/fleet/screen-outage.ts`). The CLAIM resolves that same predicate
   * with the account's `screen` pin, so the two are not one answer.
   *
   * THE SCREEN'S OWN WALL fires it too, asked FIRST (2026-11-01): the screen responder grades
   * the judgement's throw (`apps/worker/src/fleet/screen-fault.ts`) and notes a RATE or QUOTA
   * wall for the claim — a 429 or 402 on the key, the key gate's paced ladder, or the 180 s
   * deadline striking while the key gate was pacing that key. An undated 429 writes no durable
   * record (it is the key gate's to wait out, `keyRowCoolOf`), so the pool read below can never
   * see it. MEASURED: developerz-ai 2026-11-01 18:19-19:01 UTC, 27 of 27 unanswered run-start
   * screens took exactly the 180 s deadline while its zai/glm-5.3-flash key answered
   * `[1302] Rate limit reached` to the coding lanes; each spent a strike and three tasks ended
   * `failed screen_unavailable`. The key the screen ran on is walled even when another key
   * might serve, so the wait is honest; the park's backstop re-tries it.
   *
   * THE MECHANISM, and what it needs: it fires when EVERY key and model the screen could
   * use is cooled or parked ON THE DURABLE RECORD (`byok_keys.status`,
   * `byok_key_models.cooling_until`) — none at all, donated only, or all of them walled.
   * Those records are written by the screen responder's own resolver when a call walls
   * (`coolOnWall` / the per-model park, `apps/worker/src/dispatch/in-process-model.ts`,
   * reached from `fleet/screen-items.ts`). A refusal that leaves no record — a timeout, a
   * 5xx, a refusal shape the resolver does not grade as a wall — reads as a healthy pool to
   * THIS read; the undated 429 among them is the screen's own wall, above. On a key with several models, a wall on ONE model hops to a
   * live sibling inside the same call and never produces `screen_unavailable` at all; this
   * kind is the case where nothing is left to hop to.
   *
   * AND THE NULL PIN ADDS A FOURTH SHAPE, WHICH IS NOT AN OUTAGE: a valid key whose OWN
   * `model` row is PARKED, beside an UNTAGGED live row on the same key. The tag rung skips
   * a row with no tier, so unpinned nothing rescues the own model — `refuseKeyModel`
   * answers `LlmModelRefusal.Parked`, the key drops out of `order`, and the pick answers
   * `null`: to this regrade the pool holds nothing that serves. A `screen` pin NAMING that
   * live row serves it, on the pin's own rung (`keyServesModel`, no tier required) — and
   * that pin is the one the screen responder ranks on. A pin RANKS, it does not FILTER:
   * what empties the pool here is the unpinned MODEL refusal, not the ranking. So this kind
   * can fire on a pool the responder would have SERVED, and it parks the task on
   * `no_screen_key` — a hold that says the account holds no key the screen can use, which
   * on this pool is FALSE. The park is released only by a key turning `valid`
   * (`releaseHoldsOnKeyValid`) or `SCREEN_UNAVAILABLE_RECHECK_MS`, so serviceable work
   * waits on a hold that names a fact which is not true.
   *
   * WHY NOT `screen_unavailable`: that kind's three-in-a-row cap bounds a screen that keeps
   * failing on a HEALTHY pool (a timeout, a fenced request). THE MOTIVATING OUTAGE, 2026-10-17,
   * tesote: the account's router key answered no paid screen 07:00-12:00 UTC (1,858 items
   * `unavailable`, 0 paid answers; 2,063 that day), every 15-minute re-claim spent one strike,
   * and 10 tasks (9 `pr_review`, 1 scout) ended `failed screen_unavailable`; four open pull
   * requests sat unreviewed for ~24 h until an operator redispatched them. That day's records
   * held no cool and no model park for the key (no `billing.key.status_changed` or
   * `billing.key.model_parked` row 06:00-13:00), so THIS regrade would not have fired on
   * them: it is the outage the kind exists for, not a case this code would have caught on
   * that day's state. It catches the same outage once the walls are written down, which
   * today's resolver does.
   *
   * Producer:
   * The MOTHERSHIP regrades — only it reads the pool. No box stamps this code.
   *
   * Budget:
   * Uncharged, uncapped and STREAK-NEUTRAL (`unchargedOutage`): nothing ran, the task is
   * not at fault, and an outage must neither end the task nor pre-spend a later kind's cap.
   * The bound is the park.
   *
   * Park:
   * `no_screen_key` — the claim gate's hold, released by a key on the account turning
   * `valid` (`releaseHoldsOnKeyValid`), with `SCREEN_UNAVAILABLE_RECHECK_MS` as the backstop
   * for the recoveries that fire no event (a parked model or a cooling window lapsing).
   *
   * Babysit round:
   * The round never started: no key could screen its brief.
   *
   * Box-at-fault:
   * No. The account's keys.
   *
   * Failure rate:
   * Not excluded, and that is not a judgement: the rate reads the box's `error` CODE, and
   * the box sent `screen_unavailable` (already excluded). This value is never on the wire
   * — `repo_env_down`'s position, for the same reason.
   *
   * Alert sentence:
   * Names the wait and its one cause: no key on the account the screen can use.
   *
   * Reason sentence:
   * The same: nothing ran, nothing was charged, a usable key resumes it.
   */
  ScreenKeyUnusable: {
    value: 'screen_key_unusable',
    ordinal: 60,
    producer: platform('apps/worker/src/fleet/screen-outage.ts#screenOutageKind'),
    budget: unchargedOutage(),
    park: {
      reason: TaskHoldReason.NoScreenKey,
      backstopMs: SCREEN_UNAVAILABLE_RECHECK_MS,
      honorsStatedReset: false,
    },
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        'The intake screen could not judge this task’s text because the provider refused its key on a rate or quota limit, or no key on the account can serve it right now (every key is cooling, walled or missing), so nothing ran and no attempt was charged. The task waits, it does not fail: it is retried on its own when the limit lifts or a key turns usable. Check the account’s keys if the wait goes on.',
      unclaimed:
        'The intake screen’s key was rate or quota limited, or no key on the account was usable, so this task’s text was not judged, nothing ran and nothing was charged. It waits and resumes on its own.',
      ran: 'The intake screen could not judge this task’s text because the provider refused its key on a rate or quota limit, or no key on the account could serve it, so no model read it and no attempt was charged. The task is waiting, not failed: it resumes on its own when the limit lifts or a key on the account is usable again.',
    },
  },
  /**
   * A PUSHED RUN WHOSE PULL REQUEST COULD NOT BE PROVEN (#6096). The coding run committed and
   * pushed its branch, and GitHub's PR listing could not then say whether this task's pull
   * request exists: the lookup failed (a 5xx, a timeout, an unreadable payload) or stayed
   * `absence_unproven` (`apps/runner/src/modes/worker/pr-lookup.ts`). Before this kind the run
   * COMPLETED (`pr_unverified`/`lookup_failed`) and the task settled with no PR and nothing to
   * retry it — tsk_57937d32 on tesote/tesote.ai, 2026-10-08, its pushed branch lost.
   *
   * Producer:
   * `apps/runner/src/modes/worker/outcome.ts#PrUnprovenError`.
   *
   * Budget:
   * Uncharged, capped at three in a row. Nothing is the task's fault — the work is pushed and
   * gate-green — and the retry adopts the pushed branch and goes straight to the open stage
   * (`team/session/reopen.ts`), which re-asks. The cap names the failure it prevents: a
   * GitHub listing that stays unprovable would otherwise re-pend the task forever at zero
   * attempts spent; three in a row end it where a person sees it.
   *
   * Babysit round:
   * Not a babysit producer: the run that opens a PR, not a round on one.
   *
   * Box-at-fault:
   * No. GitHub's answer, read the same from every box.
   *
   * Alert sentence:
   * Names the pushed branch's state and the retry that will re-ask.
   *
   * Reason sentence:
   * The same: the work is pushed, nothing was charged, it retries on its own.
   */
  PrUnproven: {
    value: 'pr_unproven',
    ordinal: 57,
    producer: wire('apps/runner/src/modes/worker/outcome.ts#PrUnprovenError'),
    budget: uncharged(3),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Uncharged,
    resumeParkable: false,
    failureRateExcluded: true,
    copy: {
      alert:
        'The run pushed its branch, but GitHub could not confirm whether this task’s pull request exists, so none was opened and no attempt was charged. It retries on its own and opens the pull request from the pushed branch; three such failures in a row end it.',
      unclaimed:
        'An earlier run pushed this task’s branch but GitHub could not confirm its pull request, and no run has picked the task up since it was queued again. Nothing was charged.',
      ran: 'The run pushed its branch, but GitHub could not confirm whether this task’s pull request exists, so none was opened and no attempt was charged. The task retries on its own and opens the pull request from the pushed branch, and stops after three such failures in a row.',
    },
  },
  /**
   * THE SCOUT'S MODEL ANSWER WAS CUT OFF (#6765, R4: no fallback plan). The decision or
   * plan call's `response` envelope was right but stopped at the output ceiling, or the
   * provider closed the stream (`StructuredTruncationError`,
   * `apps/runner/src/llm/finish-reason.ts`). Before R4 the scout floored it into a one-group plan; now the run
   * ends failed with the reason, and redispatch retries it.
   *
   * Producer:
   * `ScoutRunFailedError` with reason `output_truncated`, graded by `SCOUT_FAILURE_KIND`.
   *
   * Budget:
   * `survey_empty`'s: charged, the full ladder, `Fail` on exhaustion. The run spent the
   * customer's key before the cut was knowable. Distinct from `provider_non_answer`: the
   * envelope was right but cut, which a narrower ask or a model with more output room
   * clears.
   *
   * Babysit round:
   * Copied from `survey_empty` (`Charged`): a model answering short is a hypothesis.
   *
   * Box-at-fault:
   * No. The cut is the model's output ceiling, the same from every box.
   */
  ScoutOutputTruncated: {
    value: 'scout_output_truncated',
    ordinal: 68,
    producer: wire('apps/runner/src/modes/scout/brain/run.ts#ScoutRunFailedError'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The scout's model answer was cut off before it finished: it spent its output limit, or the provider closed the stream. No plan was built from a partial answer. The row retries on its next attempt; a narrower ask, or a model with more output room, clears it.",
      unclaimed:
        "No box claimed this — the scout's last model answer was cut off before it finished, so no plan was built and the row waits for its next attempt.",
      ran: "The model's answer was cut off before it finished: it spent its output limit, or the provider closed the stream. It retries; a narrower ask, or a model with more output room, clears it.",
    },
  },
  /**
   * THE PLAN THE SCOUT'S MODEL WROTE FAILED THE PLATFORM'S CHECKS (#6765, R4). The in-box
   * plan call's answer had a cyclic or dangling dependency or failed the envelope gate
   * (`dag_validation`, `authorPlan`), or folding an over-split plan into its last group
   * would overrun that group's `problem` or `acceptance_criteria` limit
   * (`plan_group_over_cap`, `capGroups`). Before R4 both floored into a one-group plan.
   *
   * Producer:
   * `ScoutRunFailedError` with reason `dag_validation` or `plan_group_over_cap`.
   *
   * Budget:
   * `survey_empty`'s: charged, the full ladder, `Fail` on exhaustion — the model's answer,
   * re-asked on the next attempt.
   *
   * Babysit round:
   * Copied from `survey_empty` (`Charged`).
   *
   * Box-at-fault:
   * No. The plan's shape, judged the same on every box.
   */
  ScoutPlanInvalid: {
    value: 'scout_plan_invalid',
    ordinal: 69,
    producer: wire('apps/runner/src/modes/scout/brain/run.ts#ScoutRunFailedError'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The plan the scout's model wrote failed the platform's checks — a cyclic or dangling dependency or an envelope-gate failure, or an over-split plan that could not fold into its last group within that group's limits — so nothing was dispatched. The row retries on its next attempt.",
      unclaimed:
        "No box claimed this — the scout's last plan failed the platform's checks, so nothing was dispatched and the row waits for its next attempt.",
      ran: "The plan the model wrote failed the platform's checks: a cyclic or dangling dependency or an envelope-gate failure (`dag_validation`), or folding an over-split plan into its last group would overrun that group's `problem` or `acceptance_criteria` limit (`plan_group_over_cap`). It retries.",
    },
  },
  /**
   * THE SCOUT'S HAND-OFF TO THE PLANNER WAS TOO LARGE (#6765). On a `planner_handoff`
   * assignment the scout hands a doable ask to the Planner as one `scout_doable` run event,
   * held strictly under `SCOUT_DOABLE_MAX_BYTES`
   * (`packages/domain/src/value-objects/run-event-limits.ts`) so the stored row is never cut. Over it, the survey brief is
   * condensed once; still over it, the run ends here.
   *
   * Producer:
   * `ScoutRunFailedError` with reason `handoff_oversize`
   * (`apps/runner/src/modes/scout/brain/handoff.ts`).
   *
   * Budget:
   * `survey_empty`'s: charged, the full ladder, `Fail` on exhaustion. The survey and the
   * decision call spent the customer's key.
   *
   * Babysit round:
   * Copied from `survey_empty` (`Charged`).
   *
   * Box-at-fault:
   * No. The size is the ask's and the survey's, the same from every box.
   */
  ScoutHandoffOversize: {
    value: 'scout_handoff_oversize',
    ordinal: 72,
    producer: wire('apps/runner/src/modes/scout/brain/run.ts#ScoutRunFailedError'),
    budget: charged(TASK_MAX_ATTEMPTS, TaskExhaustionAction.Fail),
    park: null,
    boxLocal: false,
    blamesBox: false,
    babysitRound: BabysitRoundCharge.Charged,
    resumeParkable: false,
    failureRateExcluded: false,
    copy: {
      alert:
        "The scout's findings were too large to hand to the Planner even after condensing them once, so no plan was asked for. The row retries on its next attempt; an ask that keeps doing this is too wide to plan in one go.",
      unclaimed:
        "No box claimed this — the scout's last findings were too large to hand to the Planner, so no plan was asked for and the row waits for its next attempt.",
      ran: "The scout's findings were too large to hand to the Planner even after condensing. Narrow the ask.",
    },
  },
} as const satisfies Record<string, FailureKindRow>;
